import type { AttachmentMeta, ChainEmail } from '../types/portal';

/**
 * One attachment rolled up from a chain, with provenance intact.
 *
 * ``emailId`` is the message the attachment lives on (needed to hit the
 * `/api/portal/email/{id}/attachment/{name}` download endpoint).
 * ``fromName`` / ``receivedAt`` let the UI render a "from reply on Apr 15"
 * style badge so users never lose track of where a file came from.
 */
export interface ChainAttachmentItem {
  attachment: AttachmentMeta;
  emailId: string;
  fromName: string;
  receivedAt: string | null;
  isSentReply: boolean;
}

/** Dedup key — matches the backend rule in
 *  services/orders/chain_attachments.aggregate_chain_attachments. */
function dedupKey(att: AttachmentMeta): string {
  return `${att.filename}::${att.size ?? 0}`;
}

/**
 * Aggregate attachments across every email in a chain.
 *
 * Iteration order is *oldest-first* so the earliest occurrence of a
 * filename wins (e.g. the original PO rather than a forwarded copy).
 * Sent replies are included — if a CSR attached a file on their reply
 * it still belongs in the packet view.  The API currently returns empty
 * attachments for SentEmail, so in practice this is a no-op for sent
 * messages, but the loop handles them for future-proofing.
 */
export function rollupChainAttachments(
  emails: ChainEmail[],
): ChainAttachmentItem[] {
  // Chain emails arrive newest-first from the backend; reverse for the
  // "oldest wins" dedup to be meaningful.
  const chronological = [...emails].sort((a, b) => {
    const ax = a.received_at || '';
    const bx = b.received_at || '';
    if (ax === bx) return 0;
    return ax < bx ? -1 : 1;
  });

  const seen = new Set<string>();
  const out: ChainAttachmentItem[] = [];

  for (const email of chronological) {
    const attachments = email.attachments || [];
    for (const att of attachments) {
      const key = dedupKey(att);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({
        attachment: att,
        emailId: email.id,
        fromName: email.from_name,
        receivedAt: email.received_at,
        isSentReply: !!email.is_sent_reply,
      });
    }
  }

  return out;
}
